import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useLazyQuery, NetworkStatus } from '@apollo/client';
import { useSelector } from 'react-redux';
import * as Q from '../../../constants/graphqlQueries';
import CollapsibleTable from './Grid/ReportingGrid';
import CustomSpinner from '../../components/LoadingScreens/CustomSpinner';
import './Reporting.css';
import Search from './Search/Search';
import UserRoles from '../../../constants/userRoles';

const hiddenFields = ['RowNumber', 'WeekNumber', 'YearNumber'];

const useStyles = makeStyles(() => ({
  collapsibleTableBox: {
      paddingBottom: '40px',
      display: 'grid',
      gridTemplateColumns: '280px 1fr',
      gap: '12px',
      marginTop: '5px',
  }
}));

const optionLabels = {
  Search: 'Search',
  Create: 'Create'
}

const Reporting = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.User);
  const classes = useStyles();
  const [getRulesDataSecondDimension, 
    {
      data: dataRulesSecondDimension,
      loading: loadingRulesSecondDimension,
      networkStatus: secondDimensionNetworkStatus
    }
  ] = useLazyQuery(
    Q.REPORTING__RULES__SECOND__DIMENSION,
    { notifyOnNetworkStatusChange: true }
  );

  const [getTaggingFlaggingDataThirdDimension, 
    {
      data: dataTaggingFlaggingThirdDimension,
      loading: loadingTaggingFlaggingThirdDimension,
      networkStatus: thirdDimensionNetworkStatus
    }
  ] = useLazyQuery(
    Q.REPORTING__TAGGING__FLAGGING__THIRD__DIMENSION,
    { notifyOnNetworkStatusChange: true }
  );

  const [getFilteredFirstDimension, { loading: loadingFilteredCPPSFirstDimensionData, data: dataFilteredCPPSFirstDimension, networkStatus: networkStatusFirstDimension }] = useLazyQuery(Q.REPORTING__FILTERED__CPPS__FIRST__DIMENSION, { notifyOnNetworkStatusChange: true });

  const [filters, setFilters] = useState({});
  const [firstDimensionData, setFirstDimensionData] = useState([]);
  const [secondDimensionColumns, setSecondDimensionColumns] = useState([]);
  const [firstDimensionColumns, setFirstDimensionColumns] = useState([]);
  const [thirdDimensionColumns, setThirdDimensionColumns] = useState([]);
  const [gridData, setGridData] = useState({});
  const [positionToUpdateSecondDimension, setPositionToUpdateSecondDimension] = useState({});
  const [retrieveSecondDimensionDataFlag, setRetrieveSecondDimensionDataFlag] = useState(false);
  const [positionToUpdateThirdDimension, setPositionToUpdateThirdDimension] = useState({});
  const [retrieveThirdDimensionDataFlag, setRetrieveThirdDimensionDataFlag] = useState(false);
  const [loadingFirstDimensionData, setLoadingFirstDimensionData] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [menuOptions] = useState([
    {
      index: 0,
      label: optionLabels.Search,
      default: true,
    },
    {
      index: 1,
      label: optionLabels.Create,
    }
  ]);

  // Handlers
  const handleExpandOnFirstDimension = (secondDimensionPosition, clientName, programName, projectName, siteName, retrieveDataForSecondDimension) => {
    if (retrieveDataForSecondDimension) {
      setRetrieveSecondDimensionDataFlag(true);
      setPositionToUpdateSecondDimension({ position: secondDimensionPosition, clientName, programName, projectName, siteName });
    }
    else {
      setRetrieveSecondDimensionDataFlag(false);
      setPositionToUpdateSecondDimension({ position: secondDimensionPosition, clientName, programName, projectName, siteName });
    }
  }

  const retrieveDataOnSecondDimension = (thirdDimensionPosition, secondDimensionPosition, rule, clientName,
    programName, projectName, siteName, retrieveDataForThirdDimension, uniqueIDsToExclude, lastKey, nextPage) => {
    if (retrieveDataForThirdDimension) {
      setRetrieveThirdDimensionDataFlag(true);
      setPositionToUpdateThirdDimension({ position: thirdDimensionPosition, parentDimensionPosition: secondDimensionPosition,
        rule, clientName, programName, projectName, siteName, uniqueIDsToExclude, lastKey, nextPage });
    }
    else {
      setRetrieveThirdDimensionDataFlag(false);
    }
  }

  // Hooks
  // Hooks - First Dimension

  useEffect(() => {
    if (dataFilteredCPPSFirstDimension?.getReportingDataFilteredCPPSFirstDimension?.length && networkStatusFirstDimension === NetworkStatus.ready) {
      setFirstDimensionData(dataFilteredCPPSFirstDimension.getReportingDataFilteredCPPSFirstDimension);
    }
    else if (networkStatusFirstDimension === NetworkStatus.ready) {
      setFirstDimensionData([]);
    }
  }, [dataFilteredCPPSFirstDimension, networkStatusFirstDimension]);

  useEffect(() => {
    if (firstDimensionData.length > 0) {
      // Remove last element from array retrieved from Object.keys (typename)
      const columns = Object.keys(firstDimensionData[0]).slice(0, -1).filter(item => !(hiddenFields.includes(item)));

      if (columns.length) {
        setFirstDimensionColumns(columns);
      }
    }
    else {
      setFirstDimensionColumns([]);
    }
  }, [firstDimensionData]);

  useEffect(() => {
    if (firstDimensionColumns.length) {
      setGridData({ columns: firstDimensionColumns, data: firstDimensionData });
    }
    else {
      setGridData({});
    }
  }, [firstDimensionColumns]);

  // Hooks - Second & Third Dimension
  useEffect(() => {
    if (Object.keys(positionToUpdateThirdDimension).length && retrieveThirdDimensionDataFlag) {
      getTaggingFlaggingDataThirdDimension({
        variables: { 
        rule: positionToUpdateThirdDimension.rule,
        client: positionToUpdateThirdDimension.clientName,
        program: positionToUpdateThirdDimension.programName,
        project: positionToUpdateThirdDimension.projectName,
        site: positionToUpdateThirdDimension.siteName,
        lastKey: positionToUpdateThirdDimension.lastKey,
        nextPage: positionToUpdateThirdDimension.nextPage,
        uniqueIDsToExclude: positionToUpdateThirdDimension.uniqueIDsToExclude,
        startDate: filters?.dates ? filters.dates[0] : null,
        endDate: filters?.dates ? filters.dates[1] : null
      }});
    }
    else if (Object.keys(positionToUpdateThirdDimension).length && !retrieveThirdDimensionDataFlag) {
      const copyParentDimension = { ...gridData.childDimensionData }
      delete copyParentDimension[positionToUpdateThirdDimension.parentDimensionPosition].childrenDimensionData[positionToUpdateThirdDimension.position];

      setGridData({ ...gridData, childDimensionData: copyParentDimension });
    }
  }, [positionToUpdateThirdDimension, retrieveThirdDimensionDataFlag]);

  useEffect(() => {
    if (Object.keys(positionToUpdateSecondDimension).length && retrieveSecondDimensionDataFlag) {
      getRulesDataSecondDimension({
        variables: { 
          client: positionToUpdateSecondDimension.clientName,
          program: positionToUpdateSecondDimension.programName,
          project: positionToUpdateSecondDimension.projectName,
          site: positionToUpdateSecondDimension.siteName,
          startDate: filters?.dates ? filters.dates[0] : null,
          endDate: filters?.dates ? filters.dates[1] : null
      }});
    }
    else if (!retrieveSecondDimensionDataFlag) {
      const gridChildDimensionDataToUpdate = { ...gridData.childDimensionData }
      delete gridChildDimensionDataToUpdate[positionToUpdateSecondDimension.position];
      setGridData({ ...gridData, childDimensionData: gridChildDimensionDataToUpdate });
    }
  }, [positionToUpdateSecondDimension]);

  // Hook - Set ThirdDimensionData
  useEffect(() => {
    if (dataTaggingFlaggingThirdDimension?.getReportingDataTaggingFlaggingThirdDimension
    && thirdDimensionNetworkStatus === NetworkStatus.ready) {
      if (gridData.childDimensionData[positionToUpdateThirdDimension.parentDimensionPosition]) {
        const updateChildNode = {
          ...gridData.childDimensionData[positionToUpdateThirdDimension.parentDimensionPosition],
          childrenDimensionData: {
            [positionToUpdateThirdDimension.position]: {
              data: dataTaggingFlaggingThirdDimension.getReportingDataTaggingFlaggingThirdDimension.data ?? [],
              count: dataTaggingFlaggingThirdDimension.getReportingDataTaggingFlaggingThirdDimension.count ?? 0
            }
          }
        }

        const parentChildDimensionUpdate = { ...gridData.childDimensionData,
          [positionToUpdateThirdDimension.parentDimensionPosition]: updateChildNode
        }

        setGridData({
          ...gridData,
          childDimensionData: parentChildDimensionUpdate
        });
      }
    }
  }, [dataTaggingFlaggingThirdDimension, thirdDimensionNetworkStatus]);

  // Hook - Set SecondDimensionData
  useEffect(() => {
    if (dataRulesSecondDimension?.getReportingDataRulesSecondDimension
    && secondDimensionNetworkStatus === NetworkStatus.ready) {

      if  (gridData.childDimensionData !== undefined) {
        const childDimensionDataUpdated = {
          ...gridData.childDimensionData,
          [positionToUpdateSecondDimension.position]: { data: dataRulesSecondDimension.getReportingDataRulesSecondDimension }
        }

        setGridData({ ...gridData, childDimensionData: childDimensionDataUpdated });
      }
      else {
        const childDimensionData= { [positionToUpdateSecondDimension.position]: { data: dataRulesSecondDimension.getReportingDataRulesSecondDimension } }
        setGridData({ ...gridData, childDimensionData });
      }
    }
  }, [dataRulesSecondDimension, secondDimensionNetworkStatus]);

  useEffect(() => {
    if (gridData.childDimensionData !== undefined && retrieveSecondDimensionDataFlag) {
      // Remove last element from array retrieved from Object.keys (typename)
      const columns = Object.keys(gridData.childDimensionData[Object.keys(gridData.childDimensionData)[0]].data[0]).slice(0, -1).filter(item => !(hiddenFields.includes(item)));
      setSecondDimensionColumns(columns);
    }

    if (gridData.childDimensionData !== undefined && retrieveThirdDimensionDataFlag) {
      const firstElementParentDimension = Object.keys(gridData.childDimensionData);
      let firstChildrenFoundOnPosition = null;
      firstElementParentDimension.map((secondDimensionDataPosition) => {
        if (gridData.childDimensionData[secondDimensionDataPosition].childrenDimensionData !== undefined) {
          firstChildrenFoundOnPosition = secondDimensionDataPosition;
        }

        return null;
      });

      if (firstChildrenFoundOnPosition !== null) {
        const firstElementChildrenDimension = Object.keys(gridData.childDimensionData[firstChildrenFoundOnPosition].childrenDimensionData)[0];
        const columns = Object.keys(gridData.childDimensionData[firstChildrenFoundOnPosition].childrenDimensionData[firstElementChildrenDimension].data[0])
            .slice(0, -1).filter(item => !(hiddenFields.includes(item)));

        setThirdDimensionColumns(columns);
      }
    }
  }, [gridData.childDimensionData]);

  useEffect(() => {
    setLoadingFirstDimensionData(loadingFilteredCPPSFirstDimensionData);
  }, [loadingFilteredCPPSFirstDimensionData]);

  useEffect(() => {
    getFilteredFirstDimension({
      variables: {
        client: filters?.client ? filters.client.DescriptionShort : null,
        startDate: filters?.dates ? filters.dates[0] : null,
        endDate: filters?.dates ? filters.dates[1] : null
      }
    });
  }, [filters]);

  const onApplyClick = (client, dates) => {
    setFilters({ client, dates });
  }

  const onClearSearch = () => {
    setFilters({});
  }

  console.log('Reporting.jsx: Current User Role:', user.Role);
  if (user.Role !== UserRoles.Admin && user.Role !== UserRoles.Owner) {
    console.log('Reporting.jsx: User does not have Admin access and Owner access.');
    setAccessDenied(true);
  }

  return (
    (() => {
        if (accessDenied) {
          return navigate(
            '/AccessDenied'
          );
        }
        
        if (menuOptions.length) {
          return <Box className={classes.collapsibleTableBox}>
            <Search
              onApplyClick={onApplyClick} isDisabled={false} onClearSearch={onClearSearch}
            />
            <Box style={{ position: 'relative' }}>
              {loadingFirstDimensionData && <CustomSpinner />}
              <CollapsibleTable
              gridData={gridData} dataIsLoading={loadingRulesSecondDimension || loadingTaggingFlaggingThirdDimension
                || loadingFilteredCPPSFirstDimensionData}
              secondDimensionColumns={secondDimensionColumns}
              thirdDimensionColumns={thirdDimensionColumns}
              handleExpandOnFirstDimension={handleExpandOnFirstDimension}
              retrieveDataOnSecondDimension={retrieveDataOnSecondDimension}
              loadingFirstDimensionData={loadingFirstDimensionData}
          /></Box></Box>
        }

        return null;
    })()
  );
};

export default Reporting;
